import { Button, Grid, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import useQueryString from '@hooks/useQueryString';

import {
  clearStorageAll,
  getStorageItem,
  STORAGE_ACTION
} from '@utils/storage';
import { SearchFilterPopper } from '..';
import useFilterList from './useFilterList';
import useGoPathAfterRender from '@hooks/useGoPathAfterRender';
import { LogoNavigator } from './LogoNavigator';
import {
  FactoronBizSearchType,
  IndustrialBizSearchType,
  InnoBizSearchType,
  MainBizSearchType,
  MiniClusterSearchType,
  VentureBizSearchType
} from '@generated/graphql';
import { SearchCategories } from './SearchCategories';
import { useEffect } from 'react';

const Header = () => {
  const { onUpdateQueryString, query, pathname } = useQueryString<{
    [key: string]: string;
  }>();
  const filterList = useFilterList(query);
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState(query['q'] ?? '');
  const navigate = useNavigate();
  const isFilterHeader = pathname.includes('industry');
  const SearchTypes = getSearchTypes(pathname);
  const categoryValues = Object.values(SearchTypes ?? []);

  const [searchType, setSearchType] = useState<string>('COMPANY_NAME');

  useEffect(() => {
    const defaultSearchType =
      categoryValues.find((value) => value.includes('COMPANY_NAME')) ?? '';

    setSearchType(defaultSearchType);
    setSearchValue('');
  }, [pathname]);

  const isChecked = Object.keys(query).every(
    (key) => key === 'page' || key === 'q'
  );

  const handleLogout = () => {
    clearStorageAll();
    navigate('/');
  };

  const handleReset = () => {
    setSearchValue('');
    navigate(`${pathname}?page=1`);
  };

  useGoPathAfterRender('/', !getStorageItem(STORAGE_ACTION.ACCESS_TOKEN));

  return (
    <Grid
      container
      justifyContent={'space-between'}
      alignContent={'flex-start'}
      height={'100vh'}
      marginBottom={2}
      padding={`8px 16px`}
    >
      <Grid container height={100} alignItems={'center'}>
        <LogoNavigator onReset={handleReset} />
        <Grid container item xs={8} justifyContent={'space-around'}>
          <Grid container item xs={8} justifyContent={'center'} gap={4}>
            <SearchCategories
              categoryValues={categoryValues}
              searchType={searchType}
              setSearchType={setSearchType}
            />

            <form
              onSubmit={(e) => {
                e.preventDefault();
                onUpdateQueryString({ q: searchValue, page: '1', searchType });
              }}
            >
              <TextField
                name={'search-q'}
                size="small"
                fullWidth
                placeholder={'검색어 입력'}
                value={searchValue}
                InputProps={{
                  startAdornment: (
                    <SearchIcon color={'action'} sx={{ marginRight: 1 }} />
                  ),
                  endAdornment: searchValue.length ? (
                    <Close
                      sx={{ cursor: 'pointer' }}
                      color={'action'}
                      onClick={() => {
                        setSearchValue('');
                      }}
                    />
                  ) : null
                }}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
            </form>
          </Grid>
          <Grid container item xs={4} justifyContent={'space-evenly'}>
            {isFilterHeader && (
              <>
                <Button
                  size={'small'}
                  variant={isChecked ? 'outlined' : 'contained'}
                  onClick={(event) => {
                    setIsOpen(!isOpen);
                    setAnchorEl(event.currentTarget);
                  }}
                >
                  필터
                </Button>
                <SearchFilterPopper
                  isOpen={isOpen}
                  anchorEl={anchorEl}
                  onClose={() => setIsOpen(false)}
                  categories={filterList}
                />
              </>
            )}
            <Button
              size={'small'}
              variant="contained"
              color={'error'}
              onClick={handleLogout}
            >
              나가기
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Outlet />
    </Grid>
  );
};

export default Header;

const getPlaceholder = (pathname: string) => {
  switch (pathname) {
    case '/industry':
    case '/innobiz':
      return '업체명';
    case '/factoryon':
      return '회사명';
    case '/mainbiz':
      return '사업자명';
    case '/venture':
      return '기업명';
    default:
      return '업체명';
  }
};

const getSearchTypes = (pathname: string) => {
  switch (pathname) {
    case '/industry':
      return IndustrialBizSearchType;
    case '/innobiz':
      return InnoBizSearchType;
    case '/factoryon':
      return FactoronBizSearchType;
    case '/mainbiz':
      return MainBizSearchType;
    case '/venture':
      return VentureBizSearchType;
    case '/miniCluster':
      return MiniClusterSearchType;
  }
};
